import type { ReactElement } from 'react'
import { useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Minus } from 'lucide-react'

import { PageHeader } from '@/components/layout/PageHeader'
import { cn } from '@/lib/utils'

interface MapLevel {
  id: string
  title: string
}

interface CampusBuilding {
  id: string
  name: string
  levels: MapLevel[]
}

interface RawMapLevel {
  id?: string
  title?: string
}

interface MapMessageData {
  type?: string
  payload?: string
}

interface MapplicWindow extends Window {
  selectLevel?: (levelId: string) => void
  map?: {
    moveTo?: (x: number, y: number, scale: number, duration: number) => void
    fitscale?: number
  }
}

const RAW_MAP_URL: string = import.meta.env.VITE_MAP_URL ?? '/corvinus-map/index.html'

const MAP_LOAD_FALLBACK_MS = 10000

const GUARD_SCRIPT = `
  (function () {
    try {
      history.pushState = function () {};
      history.replaceState = function () {};
    } catch (e) {}
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (el) {
        var href = el.getAttribute('href') || '';
        if (href.charAt(0) === '#') e.preventDefault();
      }
    }, true);
  })();
`

const INJECTED_STYLE = `
  html, body { width: 100vw !important; height: 100vh !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; background-color: #F2F2F2 !important; }
  .mapplic-zoom-buttons, .mapplic-clear-button, .mapplic-sidebar { display: none !important; }
  .mapplic-map.mapplic-dragging { transition: none !important; }
`

const INJECTED_SCRIPT = `
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'ZOOM') {
      var selector = e.data.direction === 'in'
        ? '.mapplic-zoomin-button, .mapplic-zoom-in, .mapplic-zoomin'
        : '.mapplic-zoomout-button, .mapplic-zoom-out, .mapplic-zoomout';
      var btn = document.querySelector(selector);
      if (btn) btn.click();
    }
  });

  (function poll(){
    try {
      if (window.map && window.map.data && window.map.data.levels && window.map.data.levels.length) {
        window.dispatchEvent(new Event('resize'));
        setTimeout(function() {
          var payload = JSON.stringify(window.map.data.levels);
          window.parent.postMessage({ type: 'MAP_DATA_READY', payload: payload }, '*');
        }, 100);
        return;
      }
    } catch (e) {}
    setTimeout(poll, 250);
  })();
`

const buildSrcDoc = (html: string, mapUrl: URL): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('base').forEach((el) => {
    el.remove()
  })

  const base = doc.createElement('base')
  base.setAttribute('href', mapUrl.href)

  const guard = doc.createElement('script')
  guard.textContent = GUARD_SCRIPT

  doc.head.prepend(base, guard)

  const style = doc.createElement('style')
  style.textContent = INJECTED_STYLE
  doc.head.appendChild(style)

  const script = doc.createElement('script')
  script.textContent = INJECTED_SCRIPT
  doc.body.appendChild(script)

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`
}

const parseBuildings = (rawLevels: RawMapLevel[]): CampusBuilding[] => {
  const buildingMap: Record<string, CampusBuilding> = {}

  if (!Array.isArray(rawLevels)) return []

  rawLevels.forEach((level) => {
    const levelId = level.id ?? 'egyeb-szint'
    const parts = levelId.split(/[-_]/)
    const bCode = parts[0] ? parts[0].toUpperCase() : 'EGYÉB'

    let bName = bCode
    if (bCode === 'E') bName = 'E épület (Fővám)'
    else if (bCode === 'C') bName = 'C épület (Közraktár)'
    else if (bCode === 'S' || bCode === 'SOHAZ') bName = 'Sóház'
    else if (bCode === 'G' || bCode === 'GELLERT') bName = 'Gellért Campus'

    let building = buildingMap[bCode]
    if (!building) {
      building = { id: bCode, name: bName, levels: [] }
      buildingMap[bCode] = building
    }

    building.levels.push({
      id: levelId,
      title: level.title ?? levelId,
    })
  })

  return Object.values(buildingMap)
}

export default function InteractiveMapPage(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [srcDoc, setSrcDoc] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const [isMapReady, setIsMapReady] = useState(false)
  const [buildings, setBuildings] = useState<CampusBuilding[]>([])
  const [activeBuildingId, setActiveBuildingId] = useState<string | null>(null)
  const [activeLevelId, setActiveLevelId] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    const load = async () => {
      setSrcDoc(null)
      setLoadError(false)
      setIsMapReady(false)

      try {
        const mapUrl = new URL(RAW_MAP_URL, window.location.href)
        const response = await fetch(mapUrl.href, { signal: controller.signal })
        if (!response.ok) {
          throw new Error(`Map HTML letöltési hiba: ${String(response.status)}`)
        }
        const html = await response.text()
        setSrcDoc(buildSrcDoc(html, mapUrl))
      } catch (e) {
        if (controller.signal.aborted) return
        console.error('Hiba a térkép betöltésekor:', e)
        setLoadError(true)
      }
    }

    void load()

    return () => {
      controller.abort()
    }
  }, [reloadKey])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      const data = event.data as MapMessageData | null | undefined

      if (data && data.type === 'MAP_DATA_READY' && data.payload) {
        try {
          const rawLevels = JSON.parse(data.payload) as RawMapLevel[]

          if (Array.isArray(rawLevels) && rawLevels.length > 0) {
            const parsedBuildings = parseBuildings(rawLevels)
            setBuildings(parsedBuildings)

            if (parsedBuildings.length > 0) {
              const firstBuilding = parsedBuildings[0]
              if (firstBuilding) {
                setActiveBuildingId(firstBuilding.id)
                const firstLevelId = firstBuilding.levels[0]?.id ?? null
                setActiveLevelId(firstLevelId)

                const mWindow = iframeRef.current.contentWindow as MapplicWindow | null | undefined
                if (mWindow && mWindow.selectLevel && firstLevelId) {
                  mWindow.selectLevel(firstLevelId)

                  setTimeout(() => {
                    if (mWindow.map && mWindow.map.moveTo && mWindow.map.fitscale !== undefined) {
                      mWindow.map.moveTo(0.5, 0.5, mWindow.map.fitscale, 300)
                    }
                  }, 150)
                }
              }
            }
            setIsMapReady(true)
          }
        } catch (e) {
          console.error('Hiba a map adatok feldolgozásakor:', e)
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  useEffect(() => {
    if (srcDoc === null) return
    const fallbackTimer = setTimeout(() => {
      setIsMapReady(true)
    }, MAP_LOAD_FALLBACK_MS)

    return () => {
      clearTimeout(fallbackTimer)
    }
  }, [srcDoc])

  const handleRetry = useCallback(() => {
    setReloadKey((k) => k + 1)
  }, [])

  const handleLevelSelect = (levelId: string) => {
    setActiveLevelId(levelId)
    try {
      const mWindow = iframeRef.current?.contentWindow as MapplicWindow | null | undefined
      if (mWindow?.selectLevel) {
        mWindow.selectLevel(levelId)

        setTimeout(() => {
          if (mWindow.map && mWindow.map.moveTo && mWindow.map.fitscale !== undefined) {
            mWindow.map.moveTo(0.5, 0.5, mWindow.map.fitscale, 300)
          }
        }, 50)
      }
    } catch (e) {
      console.error('Hiba a szintváltáskor:', e)
    }
  }

  const handleBuildingSelect = (buildingId: string) => {
    setActiveBuildingId(buildingId)
    const building = buildings.find((b) => b.id === buildingId)

    if (building && building.levels.length > 0) {
      const firstLevel = building.levels[0]
      if (firstLevel) {
        handleLevelSelect(firstLevel.id)
      }
    }
  }

  const handleZoom = (direction: 'in' | 'out') => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'ZOOM', direction },
      window.location.origin,
    )
  }

  const activeBuilding = buildings.find((b) => b.id === activeBuildingId)

  return (
    <div
      data-node-id="map-page-wrapper"
      className="bg-background flex h-dvh w-full flex-col overflow-hidden"
    >
      <div className="w-full shrink-0">
        <PageHeader
          nodeId="map-page-header"
          title={t('nav.map', 'Térkép')}
          onBack={() => void navigate(-1)}
        />
      </div>

      {isMapReady && buildings.length > 0 && (
        <div className="w-full shrink-0 scrollbar-none overflow-x-auto px-4 pt-1 pb-3">
          <div className="flex gap-3">
            {buildings.map((building) => {
              const isSelected = building.id === activeBuildingId
              return (
                <button
                  key={building.id}
                  onClick={() => {
                    handleBuildingSelect(building.id)
                  }}
                  className={cn(
                    'rounded-full border px-5 py-2 text-sm font-semibold whitespace-nowrap shadow-sm transition-all',
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-card-foreground hover:bg-muted',
                  )}
                >
                  {building.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="relative w-full flex-1">
        <div className="absolute inset-0 mx-4 mb-2 overflow-hidden rounded-xl border bg-[#F2F2F2] shadow-sm">
          {loadError && (
            <div className="bg-background/80 absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 backdrop-blur-sm">
              <div className="text-sidebar-text-secondary">
                {t('app.map.error', 'A térkép betöltése nem sikerült.')}
              </div>
              <button
                onClick={handleRetry}
                className="bg-primary text-primary-foreground rounded-full px-5 py-2 text-sm font-semibold shadow-sm"
              >
                {t('app.map.retry', 'Újrapróbálás')}
              </button>
            </div>
          )}

          {!isMapReady && !loadError && (
            <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
              <div className="text-sidebar-text-secondary">
                {t('app.map.loading', 'Térkép betöltése...')}
              </div>
            </div>
          )}

          {srcDoc !== null && (
            <iframe
              ref={iframeRef}
              srcDoc={srcDoc}
              title={t('nav.map', 'Térkép')}
              className="absolute inset-0 block h-full w-full border-none"
              allowFullScreen
            />
          )}

          {isMapReady && srcDoc !== null && (
            <div className="pointer-events-auto absolute right-6 bottom-6 z-10 flex flex-col gap-2">
              <button
                onClick={() => {
                  handleZoom('in')
                }}
                className="bg-card hover:bg-muted text-card-foreground flex h-11 w-11 items-center justify-center rounded-full border shadow-md transition-all"
                aria-label={t('app.map.zoomIn', 'Nagyítás')}
              >
                <Plus size={22} strokeWidth={2.5} />
              </button>
              <button
                onClick={() => {
                  handleZoom('out')
                }}
                className="bg-card hover:bg-muted text-card-foreground flex h-11 w-11 items-center justify-center rounded-full border shadow-md transition-all"
                aria-label={t('app.map.zoomOut', 'Kicsinyítés')}
              >
                <Minus size={22} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      </div>

      {isMapReady && activeBuilding && activeBuilding.levels.length > 0 && (
        <div className="flex w-full shrink-0 scrollbar-none justify-center overflow-x-auto px-4 pt-2 pb-6">
          <div className="bg-card pointer-events-auto flex gap-1 rounded-full border p-1.5 shadow-sm">
            {activeBuilding.levels.map((level) => {
              const isSelected = level.id === activeLevelId
              return (
                <button
                  key={level.id}
                  onClick={() => {
                    handleLevelSelect(level.id)
                  }}
                  className={cn(
                    'flex h-10 min-w-10 shrink-0 items-center justify-center rounded-full px-4 text-sm font-bold transition-all',
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  {level.title}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
